<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useKanbanStore } from 'dashboard/stores/kanban';
import { useAcesso } from 'dashboard/composables/useAcesso';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

// CRM-01g: builds a card from scratch. The phone number is mandatory because the
// card is still the conversation — without a channel there is no conversation, and
// without a conversation there is no card.
const emit = defineEmits(['created', 'openCard']);

const { t } = useI18n();
const kanban = useKanbanStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');
const agentBots = useMapGetter('agentBots/getBots');
const currentRole = useMapGetter('getCurrentRole');

const dialogRef = ref(null);
// The parser does not emit on its own: it exposes `sendMessage`, and whoever
// hosts it decides when to call it. Here that moment is the Create click.
const templateParserRef = ref(null);
const form = ref({});
const isSaving = ref(false);
const errorMessage = ref('');
// Filled by the lookup: an existing contact with this phone, and its card if the
// board already holds one.
const duplicate = ref(null);

// --- bot (g.6) --------------------------------------------------------------
// An inbox with a bot answers by itself when the contact replies. Checked is what
// every card did before this switch existed, so on stays the default and the
// operator opts out, never in.
const botEnabled = ref(true);

// --- disparo (g.4) ----------------------------------------------------------
// Outside the 24h WhatsApp window — which a brand-new contact always is — only an
// approved template goes through. So this is a template picker, never a text box.
const wantsDisparo = ref(false);
const selectedTemplateName = ref('');
// Set by the parser once the variables are filled: { message, templateParams }.
const templatePayload = ref(null);

const INPUT_CLASS =
  'w-full h-9 px-2.5 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand';

const stages = computed(() => kanban.stages);

// The pipeline names the inbox when its intake filter leaves exactly one option;
// anything else is a real question and the operator answers it.
const pipelineInboxIds = computed(
  () => kanban.activePipeline?.intake_filters?.inbox_ids || []
);

const availableInboxes = computed(() => {
  const allowed = pipelineInboxIds.value;
  const all = inboxes.value || [];
  if (!allowed.length) return all;
  return all.filter(inbox => allowed.includes(inbox.id));
});

const needsInboxChoice = computed(() => availableInboxes.value.length !== 1);

const selectedInbox = computed(
  () =>
    (inboxes.value || []).find(inbox => inbox.id === form.value.inbox_id) ||
    null
);

// Only approved templates can be sent; the rest would fail at the provider.
// Compared without case because the two sources disagree: Meta returns
// "APPROVED", while the seeds and factories in this repo write "approved". A
// strict comparison matched the fixtures and silently hid every real template.
const availableTemplates = computed(() =>
  (selectedInbox.value?.message_templates || []).filter(
    template => template.status?.toLowerCase() === 'approved'
  )
);

// D4: spending a paid message is the one part of creation that is not open to
// every agent. ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, é do `outbounds:send`, como no servidor
// (`kanban/cards_controller`, o template no card novo); desligada, do administrador, como hoje.
const { decide } = useAcesso();
const canSendDisparo = computed(() =>
  decide('outbounds:send', () => currentRole.value === 'administrator')
);

const disparoAvailable = computed(
  () => canSendDisparo.value && availableTemplates.value.length > 0
);

const isWhatsappInbox = computed(
  () => selectedInbox.value?.channel_type === INBOX_TYPES.WHATSAPP
);

// "Na volta" only means something when something went out: without a disparo the
// contact has nothing to reply to, and only WhatsApp sends one from here. Off
// either way, the switch would be a decision about a moment that never comes.
const botOptionVisible = computed(
  () => isWhatsappInbox.value && wantsDisparo.value
);

const selectedTemplate = computed(
  () =>
    availableTemplates.value.find(
      template => template.name === selectedTemplateName.value
    ) || null
);

const agentOptions = computed(() => [
  ...(agents.value || []).map(agent => ({
    key: `User:${agent.id}`,
    label: agent.name,
    id: agent.id,
    type: 'User',
  })),
  ...(agentBots.value || []).map(bot => ({
    key: `AgentBot:${bot.id}`,
    label: bot.name,
    id: bot.id,
    type: 'AgentBot',
  })),
]);

const canSubmit = computed(
  () =>
    Boolean(form.value.phone_number?.trim()) &&
    Boolean(form.value.stage_id) &&
    Boolean(form.value.inbox_id) &&
    // Deliberately NOT gated on the template being filled. A disabled button
    // with no reason is indistinguishable from a broken one — the check moved
    // into onSubmit, where it can say what is missing.
    !isSaving.value
);

const open = (stageId = null) => {
  form.value = {
    name: '',
    phone_number: '',
    email: '',
    title: '',
    stage_id: stageId || stages.value[0]?.id || null,
    assignee_key: '',
    inbox_id:
      availableInboxes.value.length === 1 ? availableInboxes.value[0].id : null,
  };
  duplicate.value = null;
  errorMessage.value = '';
  botEnabled.value = true;
  wantsDisparo.value = false;
  selectedTemplateName.value = '';
  templatePayload.value = null;
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

// The header button is there from the first paint, so open() can run before the board has
// loaded, and it reads the defaults once. Whatever it could not fill then is filled when the
// data arrives — never over a choice the operator already made, as long as it is still one
// of the options. Left empty, the field was the only thing keeping Criar disabled, and
// nothing on screen said so; left on an inbox the board does not admit, with the selector
// gone, Criar sent it and the server refused.
watch(stages, value => {
  if (!value.length || value.some(stage => stage.id === form.value.stage_id))
    return;
  form.value.stage_id = value[0].id;
});

watch(availableInboxes, value => {
  if (value.some(inbox => inbox.id === form.value.inbox_id)) return;
  form.value.inbox_id = value.length === 1 ? value[0].id : null;
});

// Warn while the operator is still typing, not after the fact. A repeated number
// reuses the existing contact — it never creates a second one.
const checkDuplicate = async () => {
  const phone = form.value.phone_number?.trim();
  if (!phone) {
    duplicate.value = null;
    return;
  }
  try {
    duplicate.value = await kanban.lookupContactByPhone(phone);
  } catch {
    duplicate.value = null;
  }
};

watch(
  () => form.value.stage_id,
  () => {
    errorMessage.value = '';
  }
);

// The parser hands back both the rendered text and the params the provider needs.
const onTemplateFilled = payload => {
  templatePayload.value = payload;
};

watch(selectedTemplateName, () => {
  templatePayload.value = null;
});

watch(wantsDisparo, value => {
  if (!value) {
    selectedTemplateName.value = '';
    templatePayload.value = null;
  }
});

// Back to the default the moment the switch leaves the screen, so an operator who
// unchecked it and then dropped the disparo cannot ship a hidden `false`.
watch(botOptionVisible, visible => {
  if (!visible) botEnabled.value = true;
});

const errorFor = error => {
  const code = error?.response?.data?.error;
  if (code === 'already_on_board')
    return t('KANBAN.CREATE_CARD.ERROR_ON_BOARD');
  if (code === 'not_admissible')
    return t('KANBAN.CREATE_CARD.ERROR_NOT_ADMISSIBLE');
  if (code === 'inbox_required')
    return t('KANBAN.CREATE_CARD.ERROR_INBOX_REQUIRED');
  if (code === 'disparo_forbidden')
    return t('KANBAN.CREATE_CARD.ERROR_DISPARO_FORBIDDEN');
  return (
    error?.response?.data?.message || t('KANBAN.CREATE_CARD.ERROR_GENERIC')
  );
};

// Asks the parser to validate and hand over the filled template. It emits
// synchronously, so the payload is available as soon as the call returns —
// and stays null when its own validation refused.
const collectTemplate = () => {
  templatePayload.value = null;
  templateParserRef.value?.sendMessage();
  return templatePayload.value;
};

const onSubmit = async () => {
  if (!canSubmit.value) return;
  errorMessage.value = '';

  if (wantsDisparo.value && !collectTemplate()) {
    // With a template on screen, the parser already says which field is empty;
    // saying it again here put two messages about the same thing (CRM-04 TELA-6).
    if (!templateParserRef.value)
      errorMessage.value = t('KANBAN.CREATE_CARD.ERROR_TEMPLATE_REQUIRED');
    return;
  }

  isSaving.value = true;
  try {
    const payload = {
      stage_id: form.value.stage_id,
      inbox_id: form.value.inbox_id,
      title: form.value.title.trim() || null,
      contact: {
        name: form.value.name.trim(),
        phone_number: form.value.phone_number.trim(),
        email: form.value.email.trim() || null,
      },
    };
    if (form.value.assignee_key) {
      const chosen = agentOptions.value.find(
        option => option.key === form.value.assignee_key
      );
      if (chosen) payload.assignee = { id: chosen.id, type: chosen.type };
    }
    // Sent only when the operator could actually see the switch; otherwise the
    // server keeps its own default, which is the bot running.
    if (botOptionVisible.value) payload.bot_enabled = botEnabled.value;
    if (wantsDisparo.value && templatePayload.value) {
      payload.template = {
        message: templatePayload.value.message,
        template_params: templatePayload.value.templateParams,
      };
    }
    const card = await kanban.createCard(payload);
    emit('created', card);
    close();
  } catch (error) {
    errorMessage.value = errorFor(error);
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('KANBAN.CREATE_CARD.TITLE')"
    :confirm-button-label="$t('KANBAN.CREATE_CARD.SUBMIT')"
    :is-loading="isSaving"
    :disable-confirm-button="!canSubmit"
    overflow-y-auto
    @confirm="onSubmit"
  >
    <div class="flex flex-col gap-4">
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ $t('KANBAN.CREATE_CARD.PHONE') }}
        <input
          v-model="form.phone_number"
          type="tel"
          :placeholder="$t('KANBAN.CREATE_CARD.PHONE_PLACEHOLDER')"
          :class="INPUT_CLASS"
          @blur="checkDuplicate"
        />
      </label>

      <!-- The duplicate is not an error: the contact is reused, which is the whole
           point of D1. Say so plainly instead of blocking. -->
      <div
        v-if="duplicate?.contact"
        class="flex items-start gap-2 p-3 text-sm border rounded-lg border-n-amber-6 bg-n-amber-2 text-n-slate-12"
      >
        <Icon icon="i-lucide-info" class="mt-0.5 size-4 flex-shrink-0" />
        <div class="flex flex-col gap-1">
          <span>
            {{
              $t('KANBAN.CREATE_CARD.DUPLICATE', {
                name: duplicate.contact.name,
              })
            }}
          </span>
          <button
            v-if="duplicate.card"
            type="button"
            class="self-start font-medium text-n-brand hover:underline"
            @click="emit('openCard', duplicate.card.id)"
          >
            {{ $t('KANBAN.CREATE_CARD.OPEN_EXISTING') }}
          </button>
        </div>
      </div>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ $t('KANBAN.CREATE_CARD.NAME') }}
        <input v-model="form.name" type="text" :class="INPUT_CLASS" />
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ $t('KANBAN.CREATE_CARD.CARD_NAME') }}
        <input
          v-model="form.title"
          type="text"
          :placeholder="$t('KANBAN.CREATE_CARD.CARD_NAME_PLACEHOLDER')"
          :class="INPUT_CLASS"
        />
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ $t('KANBAN.CREATE_CARD.STAGE') }}
        <select v-model="form.stage_id" :class="INPUT_CLASS">
          <option v-for="stage in stages" :key="stage.id" :value="stage.id">
            {{ stage.name }}
          </option>
        </select>
      </label>

      <label
        v-if="needsInboxChoice"
        class="flex flex-col gap-1 text-sm text-n-slate-12"
      >
        {{ $t('KANBAN.CREATE_CARD.INBOX') }}
        <select v-model="form.inbox_id" :class="INPUT_CLASS">
          <option
            v-for="inbox in availableInboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
      </label>

      <!-- g.5: one list, two kinds. AssignmentService already branches on the
           type, so a human and a bot are the same choice from here. -->
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ $t('KANBAN.CREATE_CARD.ASSIGNEE') }}
        <select v-model="form.assignee_key" :class="INPUT_CLASS">
          <option value="">{{ $t('KANBAN.CREATE_CARD.NO_ASSIGNEE') }}</option>
          <option
            v-for="option in agentOptions"
            :key="option.key"
            :value="option.key"
          >
            {{ option.label }}
          </option>
        </select>
      </label>

      <!-- g.4: the disparo. Hidden entirely when it cannot happen — an operator
           without permission, or an inbox with no approved template, should not
           be offered a switch that leads nowhere. -->
      <fieldset
        v-if="disparoAvailable"
        class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak"
      >
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <Checkbox v-model="wantsDisparo" />
          {{ $t('KANBAN.CREATE_CARD.SEND_DISPARO') }}
        </label>

        <p class="text-xs text-n-slate-11">
          {{ $t('KANBAN.CREATE_CARD.DISPARO_NOTE') }}
        </p>

        <template v-if="wantsDisparo">
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ $t('KANBAN.CREATE_CARD.TEMPLATE') }}
            <select v-model="selectedTemplateName" :class="INPUT_CLASS">
              <option value="">
                {{ $t('KANBAN.CREATE_CARD.TEMPLATE_PLACEHOLDER') }}
              </option>
              <option
                v-for="template in availableTemplates"
                :key="template.id || template.name"
                :value="template.name"
              >
                {{ template.name }}
              </option>
            </select>
          </label>

          <WhatsAppTemplateParser
            v-if="selectedTemplate"
            ref="templateParserRef"
            :template="selectedTemplate"
            @send-message="onTemplateFilled"
          />

          <!-- g.6: who takes the conversation when the contact answers the
               disparo. Lives inside this block because that is the only moment it
               applies — no message out, no reply to hand to anyone. -->
          <div
            v-if="botOptionVisible"
            class="flex flex-col gap-1 pt-3 border-t border-n-weak"
          >
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <Checkbox v-model="botEnabled" />
              {{ $t('KANBAN.CREATE_CARD.BOT_ON_REPLY') }}
            </label>
            <p class="text-xs text-n-slate-11">
              {{
                botEnabled
                  ? $t('KANBAN.CREATE_CARD.BOT_ON_REPLY_NOTE')
                  : $t('KANBAN.CREATE_CARD.BOT_OFF_NOTE')
              }}
            </p>
          </div>
        </template>
      </fieldset>

      <p v-if="errorMessage" class="text-sm text-n-ruby-11" role="alert">
        {{ errorMessage }}
      </p>
    </div>
  </Dialog>
</template>
